/*
  barna flow — S4 product page behaviour (templates/product.s4.json).

  Four small pieces, each doing only what Liquid and CSS cannot. Everything scrolls by touch,
  trackpad and keyboard without them; they only add buttons and state.
    s4-gallery   syncs the thumbnail rail, the 1/6 counter and the arrows with the
                 scroll-snap track, and runs the zoom lightbox.
    s4-rail      previous / next buttons for the one-row review strips, disabled at
                 either end and hidden when everything already fits.
    s4-guard     with a Kaching mix-and-match tier whose "Select product" slots are still
                 empty, Add to cart opens the first slot's picker instead of adding the one
                 pair Kaching would otherwise put in the cart on its own.
    s4-buybar    the sticky add-to-cart bar pinned to the bottom of the screen: shows from
                 the first scroll, steps aside while the real button is in the lower half of
                 the screen or the footer is showing, presses the real button so the selected
                 Kaching tier is what gets added, mirrors that tier's name and price, and says
                 whether it ships free (or which tier does).
    s4-zoom      a magnifier on each sock photo in the Kaching bundle and its "Choose" list,
                 opening that design's photos (snippets/s4-bundle-zoom.liquid) in a dialog.
    thumbs       asks Shopify for 200px copies of the photos in Kaching's pickers.
    s4-clips     the muted customer videos: each loads only as it nears the screen, plays
                 while it shows and pauses when it leaves; its button pauses or plays it.

  Rise-on-scroll comes from assets/s3-pdp.js.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const smooth = () => (reduced.matches ? 'auto' : 'smooth');

  /* --------------------------------------------------------------- thumbs */
  // Kaching's "Choose" list loads every sock photo at full size (120–260 KB each, 1.5 MB for the
  // list) to show it 100px wide. Shopify's image server resizes on request, so the src gets
  // &width=200 as Kaching sets it: by the time the img is in the page, the full-size download
  // has already started. Only images in Kaching's pickers are touched, and a URL that already
  // names a size is left alone. If Kaching renames its classes, its photos simply load full size.
  const KACHING_THUMB = /\bkaching-bundles__choose-[\w-]*image\b/;
  const SHOPIFY_FILE = /^(https?:)?\/\/[^/]+\/(s\/files|cdn\/shop)\//;
  const thumbUrl = (value) => {
    const url = String(value);
    if (!SHOPIFY_FILE.test(url) || /[?&](width|height)=|_\d*x\d*\.\w+(\?|$)/.test(url)) return value;
    return `${url}${url.includes('?') ? '&' : '?'}width=200`;
  };
  if (!Element.prototype.setAttribute.s4Thumbs) {
    const setAttribute = Element.prototype.setAttribute;
    const withThumbs = function (name, value) {
      if (name === 'src' && this.localName === 'img' && KACHING_THUMB.test(this.className)) value = thumbUrl(value);
      return setAttribute.call(this, name, value);
    };
    withThumbs.s4Thumbs = true;
    Element.prototype.setAttribute = withThumbs;
  }

  /* -------------------------------------------------------------- gallery */
  if (!customElements.get('s4-gallery')) {
    customElements.define(
      's4-gallery',
      class S4Gallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.s4-gallery__track');
          this.slides = Array.from(this.querySelectorAll('.s4-gallery__slide'));
          this.thumbs = Array.from(this.querySelectorAll('[data-gallery-go]'));
          this.rail = this.querySelector('.s4-gallery__thumbs');
          this.counter = this.querySelector('[data-gallery-current]');
          this.dialog = this.querySelector('.s3-lightbox');
          this.zooms = Array.from(this.querySelectorAll('[data-zoom-src]'));
          this.index = 0;
          if (!this.track || this.slides.length === 0) return;

          this.thumbs.forEach((thumb) => {
            thumb.addEventListener('click', () => this.go(Number(thumb.dataset.galleryGo)));
          });
          this.querySelectorAll('[data-gallery-step]').forEach((button) => {
            button.addEventListener('click', () => this.go(this.index + Number(button.dataset.galleryStep)));
          });
          this.track.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
            event.preventDefault();
            this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
          });

          if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) this.setActive(Number(entry.target.dataset.index));
                });
              },
              { root: this.track, threshold: 0.6 }
            );
            this.slides.forEach((slide) => observer.observe(slide));
          }

          this.initLightbox();
        }

        go(index) {
          const count = this.slides.length;
          const next = (index + count) % count;
          // offsetLeft includes the track's own side padding on mobile; subtract it so the
          // frame snaps to the same edge the scroll-padding does.
          const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
          this.track.scrollTo({ left: this.slides[next].offsetLeft - pad, behavior: smooth() });
          this.setActive(next);
        }

        setActive(index) {
          if (index < 0 || index === this.index) return;
          this.index = index;
          if (this.counter) this.counter.textContent = String(index + 1);
          this.thumbs.forEach((thumb, i) => {
            if (i === index) {
              thumb.setAttribute('aria-current', 'true');
              this.revealThumb(thumb);
            } else {
              thumb.removeAttribute('aria-current');
            }
          });
          this.slides.forEach((slide, i) => {
            if (i !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
          });
        }

        // Keeps the active thumbnail inside the vertical rail without scrolling the page.
        revealThumb(thumb) {
          if (!this.rail || this.rail.scrollHeight <= this.rail.clientHeight + 1) return;
          const top = thumb.offsetTop - this.rail.offsetTop;
          if (top < this.rail.scrollTop || top + thumb.offsetHeight > this.rail.scrollTop + this.rail.clientHeight) {
            this.rail.scrollTo({ top: top - 8, behavior: smooth() });
          }
        }

        initLightbox() {
          if (!this.dialog || this.zooms.length === 0 || typeof this.dialog.showModal !== 'function') return;
          this.img = this.dialog.querySelector('.s3-lightbox__img');
          this.zoomIndex = 0;

          this.zooms.forEach((button, i) => {
            button.addEventListener('click', () => {
              this.show(i);
              this.dialog.showModal();
            });
          });
          this.dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
          this.dialog.querySelectorAll('[data-lightbox-step]').forEach((button) => {
            button.addEventListener('click', () => this.show(this.zoomIndex + Number(button.dataset.lightboxStep)));
          });
          this.dialog.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.dialog.close();
          });
          this.dialog.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') this.show(this.zoomIndex + 1);
            if (event.key === 'ArrowLeft') this.show(this.zoomIndex - 1);
          });
          this.dialog.addEventListener('close', () => {
            const slide = this.zooms[this.zoomIndex]?.closest('.s4-gallery__slide');
            if (slide) this.go(Number(slide.dataset.index));
          });
        }

        show(index) {
          const count = this.zooms.length;
          this.zoomIndex = (index + count) % count;
          const button = this.zooms[this.zoomIndex];
          this.img.src = button.dataset.zoomSrc;
          this.img.alt = button.getAttribute('aria-label') || '';
        }
      }
    );
  }

  /* ----------------------------------------------------------------- rail */
  if (!customElements.get('s4-rail')) {
    customElements.define(
      's4-rail',
      class S4Rail extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-rail-track]');
          if (!this.track) return;
          this.prev = this.querySelector('[data-rail-step="-1"]');
          this.next = this.querySelector('[data-rail-step="1"]');
          this.frame = 0;

          this.querySelectorAll('[data-rail-step]').forEach((button) => {
            button.addEventListener('click', () => this.step(Number(button.dataset.railStep)));
          });
          const queue = () => {
            if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
          };
          this.track.addEventListener('scroll', queue, { passive: true });
          window.addEventListener('resize', queue, { passive: true });
          this.update();
        }

        step(direction) {
          const item = this.track.firstElementChild;
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          const by = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
          this.track.scrollBy({ left: direction * by, behavior: smooth() });
        }

        update() {
          this.frame = 0;
          const max = this.track.scrollWidth - this.track.clientWidth;
          this.toggleAttribute('data-static', max <= 2);
          if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
          if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
        }
      }
    );
  }

  /* ---------------------------------------------------------------- guard */
  const EMPTY_SLOT = '.kaching-bundles__collection-product__choose-product-button';
  const firstEmptySlot = (buy) => buy.querySelector(`.kaching-bundles__bar--selected ${EMPTY_SLOT}`);

  const initBundleGuard = (buy) => {
    if (buy.dataset.s4GuardInit) return;
    buy.dataset.s4GuardInit = 'true';
    const button = document.getElementById(buy.dataset.s4BundleGuard);
    if (!button) return;

    const guide = (event) => {
      const slot = firstEmptySlot(buy);
      if (!slot) return;
      // Kaching and the theme's product form listen further down; stop the add here.
      event.preventDefault();
      event.stopImmediatePropagation();
      // With the pair rows in (assets/s4-pair-picker.js) Kaching's slots are hidden; the first
      // empty pair row stands in for them, and its arrow opens the same list.
      const row = buy.querySelector('.s4-pairs--ready .s4-pairrow--empty');
      const card = row?.querySelector('.s4-pairrow__card') || slot.closest('.kaching-bundles__collection-product') || slot;
      card.scrollIntoView({ behavior: smooth(), block: 'center' });
      // Once the slot has scrolled into place it pulses, and Kaching's own picker ("Choose
      // socks") opens after the first pulse, so the shopper sees which pair it's for.
      const still = reduced.matches;
      setTimeout(() => {
        card.classList.remove('s4-slot-flag');
        void card.offsetWidth; // restart the highlight if it's already running
        card.classList.add('s4-slot-flag');
        card.addEventListener('animationend', () => card.classList.remove('s4-slot-flag'), { once: true });
        setTimeout(() => (row?.querySelector('.s4-pairrow__pick') || slot).click(), still ? 0 : 750);
      }, still ? 0 : 350);
    };
    // Capture on window runs before any listener on the button or its form. The sticky bar
    // presses this same button, so it's covered too.
    window.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && event.target.closest(`#${CSS.escape(button.id)}`)) guide(event);
      },
      true
    );
    window.addEventListener(
      'submit',
      (event) => {
        if (button.form && event.target === button.form) guide(event);
      },
      true
    );
  };

  /* --------------------------------------------------------------- buybar */
  const initBuybar = (bar) => {
    if (bar.dataset.s4Init) return;
    bar.dataset.s4Init = 'true';

    const mainButton = document.getElementById(bar.dataset.target);
    const action = bar.querySelector('[data-buybar-action]');
    if (!mainButton || !action) return;

    const root = document.documentElement;
    let scrolled = false;
    let mainInView = false;
    let footerInView = false;

    /*
      Sticky columns and anchor targets further down clear the theme's fixed header through
      --s3-head-h (s3-pdp.css); it is measured because the header's height changes with the
      announcement bar. --s4-buybar-h lets the back-to-top button sit above this bar.
    */
    const themeHeader = document.querySelector('.m-header__wrapper');
    const measure = () => {
      if (themeHeader) root.style.setProperty('--s3-head-h', `${Math.round(themeHeader.offsetHeight)}px`);
      root.style.setProperty('--s4-buybar-h', `${Math.round(bar.offsetHeight)}px`);
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });

    const update = () => {
      const show = scrolled && !mainInView && !footerInView;
      if (show === bar.classList.contains('is-visible')) return;
      bar.classList.toggle('is-visible', show);
      root.classList.toggle('s4-buybar-shown', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.toggleAttribute('inert', !show);
    };

    // "As soon as they start scrolling": a few pixels, so iOS rubber-banding at the top doesn't count.
    const onScroll = () => {
      const next = window.scrollY > 8;
      if (next === scrolled) return;
      scrolled = next;
      update();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    if ('IntersectionObserver' in window) {
      /*
        Only the lower half of the screen counts: that's where the real button and this bar would
        crowd each other. Once the real button moves up past the middle the bar comes back, so it
        is gone for half a screen of scrolling rather than a whole one.
      */
      new IntersectionObserver(([entry]) => {
        mainInView = entry.isIntersecting;
        update();
      }, { rootMargin: '-50% 0px 0px 0px' }).observe(mainButton);

      const footer =
        document.querySelector('.shopify-section-group-footer-group') ||
        Array.from(document.querySelectorAll('footer')).find((el) => !el.closest('m-cart-drawer, dialog, [class*="drawer"]'));
      if (footer) {
        new IntersectionObserver(([entry]) => {
          footerInView = entry.isIntersecting;
          update();
        }).observe(footer);
      }
    }

    action.addEventListener('click', () => {
      if (mainButton.disabled) {
        const fallback = document.getElementById(bar.dataset.fallback) || mainButton;
        fallback.scrollIntoView({ behavior: smooth(), block: 'center' });
        fallback.querySelector('input[type="email"]')?.focus({ preventScroll: true });
        return;
      }
      mainButton.click();
    });

    // While the chosen tier has empty bundle slots the button opens the first one (s4-guard),
    // so it says so.
    const addLabel = action.textContent.trim();
    const slotsLabel = action.dataset.slotsLabel || '';
    const syncLabel = (selected) => {
      const label = slotsLabel && !mainButton.disabled && selected?.querySelector(EMPTY_SLOT) ? slotsLabel : addLabel;
      if (action.textContent !== label) action.textContent = label;
    };

    // Mirror the Kaching tier the shopper picked, so the bar says what the button adds.
    const title = bar.querySelector('[data-buybar-title]');
    const price = bar.querySelector('[data-buybar-price]');
    const compare = bar.querySelector('[data-buybar-compare]');
    const buy = mainButton.closest('.s3-buy') || document.querySelector('.s3-buy');
    if (!buy || !title || !price || !compare) return;
    const fallback = { title: title.textContent, price: price.textContent, compare: compare.textContent };
    const text = (el) => el?.textContent.replace(/\s+/g, ' ').trim() || '';

    /*
      Free shipping. Kaching's deal settings (a JSON <script>) tag each perk with a giftType; the
      "shipping" ones are found by id in the tier markup, so this works in every language and
      follows the deal if its tiers change. The script usually sits in the Kaching block, but on
      the Double-Lock template Kaching mounts it outside the buy box, so fall back to the page.
    */
    const ship = bar.querySelector('[data-buybar-ship]');
    const shipText = bar.querySelector('[data-buybar-ship-text]');
    const nudge = bar.querySelector('[data-buybar-nudge]');
    const nudgeText = bar.querySelector('[data-buybar-nudge-text]');
    let shippingIds = null;
    const readShippingIds = () => {
      const ids = new Set();
      const walk = (node) => {
        if (Array.isArray(node)) node.forEach(walk);
        else if (node && typeof node === 'object') {
          if (node.giftType === 'shipping' && node.id) ids.add(String(node.id));
          Object.values(node).forEach(walk);
        }
      };
      const selector = 'script.kaching-bundles-deal-block-settings';
      const scripts = buy.querySelectorAll(selector);
      (scripts.length ? scripts : document.querySelectorAll(selector)).forEach((script) => {
        try {
          walk(JSON.parse(script.textContent));
        } catch (error) {
          /* Not JSON we understand: no strip. */
        }
      });
      return ids;
    };
    const perks = (tier) => Array.from(tier.querySelectorAll('[data-free-gift-id]'));
    const shipsFree = (tier) => perks(tier).some((el) => shippingIds.has(el.dataset.freeGiftId));
    const hasGift = (tier) =>
      Boolean(tier.querySelector('.kaching-bundles__multiple-gifts-selector')) ||
      perks(tier).some((el) => !shippingIds.has(el.dataset.freeGiftId));
    let nudgeTier = null;
    const syncShipping = (selected) => {
      if (!ship || !nudge) return;
      if (!shippingIds || shippingIds.size === 0) shippingIds = readShippingIds();
      const tiers = Array.from(buy.querySelectorAll('.kaching-bundles__bar'));
      let on = '';
      let offer = '';
      nudgeTier = null;
      if (selected && shippingIds.size > 0) {
        if (shipsFree(selected)) {
          on = (hasGift(selected) && nudge.dataset.includedGift) || nudge.dataset.included || '';
        } else {
          nudgeTier = tiers.find(shipsFree) || null;
          const name = text(nudgeTier?.querySelector('.kaching-bundles__bar-title'));
          if (nudgeTier && name && nudge.dataset.nudge) offer = nudge.dataset.nudge.replace('[tier]', name);
        }
      }
      if (shipText.textContent !== on) shipText.textContent = on;
      if (nudgeText.textContent !== offer) nudgeText.textContent = offer;
      ship.hidden = !on;
      nudge.hidden = !offer;
      measure();
    };
    nudge?.addEventListener('click', () => {
      // Picks the tier the way a shopper would, through Kaching's own radio.
      const input = nudgeTier?.querySelector('input');
      if (input) input.click();
      else nudgeTier?.click();
    });

    let frame = 0;
    const sync = () => {
      frame = 0;
      const selected = buy.querySelector('.kaching-bundles__bar--selected');
      syncShipping(selected);
      syncLabel(selected);
      const tier = {
        title: text(selected?.querySelector('.kaching-bundles__bar-title')),
        price: text(selected?.querySelector('.kaching-bundles__bar-price')),
        compare: text(selected?.querySelector('.kaching-bundles__bar-full-price')),
      };
      const next = tier.title && tier.price ? tier : fallback;
      if (next.compare === next.price) next.compare = '';
      if (title.textContent !== next.title) title.textContent = next.title;
      if (price.textContent !== next.price) price.textContent = next.price;
      if (compare.textContent !== next.compare) compare.textContent = next.compare;
    };
    new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(sync);
    }).observe(buy, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
    sync();
  };

  /* ----------------------------------------------------------------- zoom */
  // A Kaching photo the viewer can open: a row of the "Choose" list, or a pair slot's photo.
  // The empty "Select product" slot has neither class, so it never gets a magnifier.
  const ZOOM_TRIGGERS = [
    '.kaching-bundles__choose-product__product-image-link',
    '.kaching-bundles__collection-product__main > .kaching-bundles__collection-product__link:first-child',
  ].join(',');
  // Kaching already links these names to the product page in a new tab; s4-pdp.css draws the ↗.
  const TITLE_LINKS = [
    'a.kaching-bundles__choose-product__product-link',
    '.kaching-bundles__collection-product__content > a.kaching-bundles__collection-product__link',
  ].join(',');
  const TITLE = '.kaching-bundles__choose-product__product-title, .kaching-bundles__collection-product__title';
  const ROW = '.kaching-bundles__choose-product__product';
  const CHOOSE = '.kaching-bundles__choose-product__product-button';
  const HEADING = '.kaching-bundles__choose-product__heading';

  // Kaching serves the same photo as files/x.webp, files/x_200x200.webp or with ?height=200.
  const fileKey = (url) =>
    (String(url).split('?')[0].split('/').pop() || '').replace(/_\d*x\d*(?=\.\w+$)/, '').toLowerCase();
  const sized = (url, width) => url.replace(/([?&])width=\d+/, `$1width=${width}`);

  const initZoom = (dialog) => {
    if (dialog.dataset.s4Init || typeof dialog.showModal !== 'function') return;
    dialog.dataset.s4Init = 'true';
    let designs;
    try {
      designs = JSON.parse(dialog.parentElement.querySelector('[data-s4-designs]')?.textContent || '[]');
    } catch {
      return;
    }
    const byHandle = new Map();
    const byFile = new Map();
    designs.forEach((design) => {
      byHandle.set(design.h, design);
      // Images come featured-first, so a photo two designs share opens the one it features.
      design.i.forEach((image) => byFile.has(fileKey(image.s)) || byFile.set(fileKey(image.s), design));
    });

    const link = dialog.querySelector('[data-s4-zoom-link]');
    const name = dialog.querySelector('[data-s4-zoom-name]');
    const track = dialog.querySelector('[data-s4-zoom-track]');
    const current = dialog.querySelector('[data-s4-zoom-current]');
    const total = dialog.querySelector('[data-s4-zoom-total]');
    const steps = Array.from(dialog.querySelectorAll('[data-s4-zoom-step]'));
    const action = dialog.querySelector('[data-s4-zoom-action]');
    const label = (template, title) => template.replace('[title]', title);
    let choose = null;

    // A product link names its design; the slot of the product on this page has no link, so
    // its photo's file name does.
    const designFor = (trigger) => {
      const handle = (trigger.getAttribute('href') || '').match(/\/products\/([^/?#]+)/)?.[1];
      if (handle && byHandle.has(decodeURIComponent(handle))) return byHandle.get(decodeURIComponent(handle));
      const img = trigger.querySelector('img');
      return img ? byFile.get(fileKey(img.getAttribute('src'))) : undefined;
    };
    const setOnce = (element, name, value) => {
      if (element.getAttribute(name) !== value) element.setAttribute(name, value);
    };

    // Kaching re-renders its rows in place (a slot's photo changes when a pair is chosen), so
    // every pass re-reads them rather than trusting an earlier mark. It runs straight from the
    // observer, before the next paint, so Kaching's own heading never shows for a frame.
    const mark = () => {
      document.querySelectorAll(ZOOM_TRIGGERS).forEach((trigger) => {
        const design = designFor(trigger);
        if (!design) {
          trigger.removeAttribute('data-s4-zoom-for');
          return;
        }
        setOnce(trigger, 'data-s4-zoom-for', design.h);
        setOnce(trigger, 'aria-label', label(dialog.dataset.view, design.t));
        setOnce(trigger, 'aria-haspopup', 'dialog');
        if (trigger.localName !== 'a') {
          setOnce(trigger, 'role', 'button');
          setOnce(trigger, 'tabindex', '0');
        }
      });
      document.querySelectorAll(TITLE_LINKS).forEach((title) => {
        const text = title.querySelector(TITLE)?.textContent.trim();
        if (text) setOnce(title, 'aria-label', label(dialog.dataset.newTab, text));
      });
      // Kaching's own heading ("Choose socks") is one English string; the theme's is translated.
      const heading = document.querySelector(HEADING);
      if (heading && dialog.dataset.heading && heading.textContent !== dialog.dataset.heading) {
        heading.textContent = dialog.dataset.heading;
      }
    };
    new MutationObserver(mark).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'href'],
    });
    mark();

    const sync = () => {
      const width = track.clientWidth || 1;
      const index = Math.round(track.scrollLeft / width);
      current.textContent = String(index + 1);
      steps.forEach((step) => {
        const to = index + Number(step.dataset.s4ZoomStep);
        step.disabled = to < 0 || to >= track.children.length;
      });
    };
    track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    const step = (by) => track.scrollBy({ left: by * track.clientWidth, behavior: smooth() });
    steps.forEach((button) => button.addEventListener('click', () => step(Number(button.dataset.s4ZoomStep))));

    const open = (design, chooseButton) => {
      const own = design.h === dialog.dataset.current;
      link.hidden = own;
      name.hidden = !own;
      link.textContent = design.t;
      link.href = design.u;
      link.setAttribute('aria-label', label(dialog.dataset.newTab, design.t));
      name.textContent = design.t;
      // Only the first photo loads now; the rest load as they are swiped to.
      track.replaceChildren(
        ...design.i.map((image, index) => {
          const slide = document.createElement('figure');
          slide.className = 's4-zoom__slide';
          const img = document.createElement('img');
          img.alt = image.a;
          img.width = 1200;
          img.height = Math.round(1200 / (image.r || 1));
          img.decoding = 'async';
          img.loading = index === 0 ? 'eager' : 'lazy';
          img.sizes = '(min-width: 768px) 560px, 92vw';
          img.srcset = [600, 900, 1200].map((width) => `${sized(image.s, width)} ${width}w`).join(', ');
          img.src = sized(image.s, 900);
          slide.append(img);
          return slide;
        })
      );
      track.style.aspectRatio = String(design.i[0]?.r || 0.8);
      total.textContent = String(design.i.length);
      dialog.classList.toggle('s4-zoom--single', design.i.length < 2);
      choose = chooseButton;
      action.textContent = choose ? dialog.dataset.choose : dialog.dataset.back;
      dialog.showModal();
      track.scrollLeft = 0;
      sync();
    };

    // Capture runs before Kaching's own handlers and before the photo's product link navigates.
    const activate = (event) => {
      const trigger = event.target instanceof Element && event.target.closest('[data-s4-zoom-for]');
      const design = trigger && byHandle.get(trigger.dataset.s4ZoomFor);
      if (!design || !dialog.isConnected) return;
      event.preventDefault();
      event.stopPropagation();
      open(design, trigger.closest(ROW)?.querySelector(CHOOSE) || null);
    };
    document.addEventListener('click', activate, true);
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.target instanceof Element && event.target.matches('[data-s4-zoom-for][role="button"]')) activate(event);
      },
      true
    );

    // "Choose this design" presses Kaching's own Choose, so Kaching records the pair as usual.
    action.addEventListener('click', () => {
      const button = choose;
      dialog.close();
      if (button?.isConnected) button.click();
    });
    dialog.querySelector('[data-s4-zoom-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.target === track) return; // the track scrolls itself with the arrow keys
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    });
    dialog.addEventListener('close', () => {
      choose = null;
    });
  };

  /* ---------------------------------------------------------------- clips */
  // Nothing is fetched with the page: once a card comes within reach of the screen its
  // first-frame still loads (Chrome would fetch a loading="lazy" image screens ahead, during the
  // page load), then its video, which plays while a third of it shows and pauses once it leaves. A paused card stays paused until its button is pressed again. With reduced motion or
  // data saver on, nothing plays or loads until the shopper presses play.
  const initClips = (box) => {
    if (box.dataset.s4Init || !('IntersectionObserver' in window)) return;
    box.dataset.s4Init = 'true';
    const auto = () => !reduced.matches && !navigator.connection?.saveData;
    const held = new WeakSet();
    const videoOf = (card) => card.querySelector('video');
    const show = (card, playing) => {
      const toggle = card.querySelector('.s4-clip__toggle');
      card.classList.toggle('is-playing', playing);
      toggle.setAttribute('aria-pressed', String(playing));
      toggle.setAttribute('aria-label', playing ? toggle.dataset.pause : toggle.dataset.play);
    };
    const load = (card) => {
      const video = videoOf(card);
      if (video.getAttribute('src') || !video.dataset.src) return;
      video.addEventListener('playing', () => card.classList.add('is-ready'), { once: true });
      video.src = video.dataset.src;
      video.load();
    };
    // The button follows the video itself, so it stays right when the browser pauses or resumes
    // it on its own (Chrome pauses silent video in a background tab and resumes it on return).
    const play = (card) => {
      const video = videoOf(card);
      load(card);
      video.muted = true;
      video.play().catch(() => {});
    };
    const pause = (card) => videoOf(card).pause();

    const cards = Array.from(box.querySelectorAll('[data-s4-clip]'));
    const still = (card) => {
      const img = card.querySelector('.s4-clip__poster');
      if (img?.dataset.src && !img.getAttribute('src')) img.src = img.dataset.src;
    };
    const near = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          still(entry.target);
          if (auto()) load(entry.target);
        }),
      { rootMargin: '400px 0px' }
    );
    const seen = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting && auto() && !held.has(entry.target)) play(entry.target);
          else if (!entry.isIntersecting && !videoOf(entry.target).paused) pause(entry.target);
        }),
      { threshold: 0.35 }
    );
    cards.forEach((card) => {
      videoOf(card).addEventListener('play', () => show(card, true));
      videoOf(card).addEventListener('pause', () => show(card, false));
      near.observe(card);
      seen.observe(card);
      card.querySelector('.s4-clip__toggle').addEventListener('click', () => {
        if (card.classList.contains('is-playing')) {
          held.add(card);
          pause(card);
        } else {
          held.delete(card);
          play(card);
        }
      });
    });
  };

  const initAll = () => {
    document.querySelectorAll('[data-s4-bundle-guard]').forEach(initBundleGuard);
    document.querySelectorAll('[data-s4-buybar]').forEach(initBuybar);
    document.querySelectorAll('[data-s4-zoom]').forEach(initZoom);
    document.querySelectorAll('[data-s4-clips]').forEach(initClips);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
